import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { ArrowLeftIcon, Loader2Icon, SkullIcon } from "lucide-react";
import { memo, useEffect, useState } from "react";
import { z } from "zod";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { request } from "@/lib/request";

const searchSchema = z.object({
  token: z.string().nonempty().catch(""),
});

export const Route = createFileRoute("/team/delete")({
  validateSearch: zodValidator(searchSchema),
  loader: async () => {
    const accessToken = localStorage.getItem("unideck.auth.accessToken");
    return { accessToken };
  },
  component: memo(RouteComponent),
});

function RouteComponent() {
  const navigate = useNavigate();
  const { token } = Route.useSearch();
  const { accessToken } = Route.useLoaderData();

  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const goBack = () => {
    navigate({
      to: "/",
      search: {
        change_password_token: undefined,
        reset_password_token: undefined,
      },
    });
  };

  const checkToken = () => {
    if (accessToken) {
      return;
    }

    localStorage.setItem("unideck.auth.dismissed", "false");
    toast.info("Please log in and try opening the link again.");
    goBack();
  };

  const handleDelete = async () => {
    if (!accessToken) {
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      await request
        .post(
          `/team/confirm-delete?token=${token}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        )
        .catch(err => {
          throw new Error(
            err.response?.data.message
              ? err.response?.data.message
              : "Failed to delete team.",
          );
        });

      toast.success("Team deleted successfully.");
      goBack();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete team.");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    checkToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog open>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Confirmation</DialogTitle>
          <DialogDescription>
            You are about to delete your team. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        {error ? (
          <Alert variant="destructive" className="mt-2">
            <AlertTitle>Delete Failed</AlertTitle>
            <AlertDescription>
              {error || "Something went wrong. Please try again."}
            </AlertDescription>
          </Alert>
        ) : null}
        <DialogFooter>
          <Button variant="default" onClick={goBack}>
            <ArrowLeftIcon />
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Loader2Icon className="animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <SkullIcon />
                Confirm & Delete
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
